import type { ResearchRunEnd } from './researchRunStore';

export interface SessionMessageLike {
  messageId?: string;
}

export interface SessionResearchRecord<TMessage extends SessionMessageLike> {
  runId: string;
  sessionId: string;
  status: 'pending' | 'completed' | 'failed';
  userMessage: TMessage;
  assistantMessage?: TMessage;
  retainedMessages?: TMessage[];
  error?: string;
  useLaneB: boolean;
  /**
   * Variante der Bahn B: 'C' lief gegen das lokale Modell, 'D' gegen ein
   * Cortecs-Modell. Ohne Angabe 'B'.
   */
  laneVariant?: 'B' | 'C' | 'D';
  /** Das Cortecs-Modell, mit dem ein Lauf der Bahn D angefordert wurde. */
  modelPreset?: string;
  userPersisted: boolean;
  /** Der Lauf am Server endete ohne Antwort; der Chat zeigt den Status unter der Frage mit „Wiederholen“ auf seiner Bahn. */
  runEnd?: ResearchRunEnd;
}

/**
 * Was ein Chat von der Recherche zeigt, der dieser Tab folgt: Frage, Antwort oder Fehler mit „Wiederholen“, bis der
 * Verlauf am Server sie trägt. Kein Laufzustand und keine Zulassung: ob ein Lauf läuft und wie viele es sein dürfen,
 * wissen allein der Server und ResearchRunStore.
 */
export class SessionResearchRecords<TMessage extends SessionMessageLike> {
  private readonly records = new Map<string, SessionResearchRecord<TMessage>>();

  begin(record: Omit<SessionResearchRecord<TMessage>, 'status' | 'assistantMessage' | 'error'>): SessionResearchRecord<TMessage> {
    const previous = this.records.get(record.sessionId);
    const retriesSameFailure = previous?.status === 'failed'
      && previous.userMessage.messageId === record.userMessage.messageId;
    const retainedMessages = previous && !retriesSameFailure
      ? [
          ...(previous.retainedMessages || []),
          previous.userMessage,
          ...(previous.assistantMessage ? [previous.assistantMessage] : []),
        ]
      : record.retainedMessages || previous?.retainedMessages;
    const pending: SessionResearchRecord<TMessage> = {
      ...record,
      ...(retainedMessages?.length ? { retainedMessages } : {}),
      status: 'pending',
    };
    this.records.set(record.sessionId, pending);
    return pending;
  }

  markUserPersisted(sessionId: string, runId: string): void {
    const record = this.current(sessionId, runId);
    if (record) this.records.set(sessionId, { ...record, userPersisted: true });
  }

  complete(sessionId: string, runId: string, assistantMessage: TMessage): void {
    const record = this.current(sessionId, runId);
    if (!record) return;
    this.records.set(sessionId, { ...record, status: 'completed', assistantMessage, error: undefined });
  }

  fail(sessionId: string, runId: string, error: string, runEnd?: SessionResearchRecord<TMessage>['runEnd']): void {
    const record = this.current(sessionId, runId);
    if (!record) return;
    this.records.set(sessionId, { ...record, status: 'failed', error, assistantMessage: undefined, ...(runEnd ? { runEnd } : {}) });
  }

  get(sessionId: string): SessionResearchRecord<TMessage> | undefined {
    return this.records.get(sessionId);
  }

  forgetCompleted(sessionId: string): void {
    if (this.records.get(sessionId)?.status === 'completed') this.records.delete(sessionId);
  }

  /** Der Chat ist gelöscht: nichts von ihm bleibt stehen. */
  forget(sessionId: string): void {
    this.records.delete(sessionId);
  }

  snapshot(): Record<string, SessionResearchRecord<TMessage>> {
    return Object.fromEntries(this.records.entries());
  }

  clear(): void {
    this.records.clear();
  }

  private current(sessionId: string, runId: string): SessionResearchRecord<TMessage> | undefined {
    const record = this.records.get(sessionId);
    return record?.runId === runId ? record : undefined;
  }
}

export class LatestSelection {
  private revision = 0;

  issue(sessionId: string): { sessionId: string; revision: number } {
    return { sessionId, revision: ++this.revision };
  }

  isCurrent(token: { sessionId: string; revision: number }, currentSessionId: string | null): boolean {
    return token.revision === this.revision && token.sessionId === currentSessionId;
  }
}

export function mergeSessionMessages<TMessage extends SessionMessageLike>(
  persisted: TMessage[],
  record?: SessionResearchRecord<TMessage>,
): TMessage[] {
  if (!record) return persisted;
  const merged = [...persisted];
  const ids = new Set(merged.map(message => message.messageId).filter(Boolean));
  for (const message of [...(record.retainedMessages || []), record.userMessage, record.assistantMessage]) {
    if (!message) continue;
    if (message.messageId && ids.has(message.messageId)) continue;
    merged.push(message);
    if (message.messageId) ids.add(message.messageId);
  }
  return merged;
}

type RunEnd = NonNullable<SessionResearchRecord<SessionMessageLike>['runEnd']>;

/**
 * Bahn A: die Endzustände der Fragen ohne Antwort (Frage-ID -> Endzustand), fortgeschrieben mit dem Lauf dieses Tabs.
 * Endet er ohne Antwort, steht sein Endzustand unter der Frage; endet er mit Antwort, fällt der Status der Frage weg.
 * Ein gescheiterter Start ohne Endzustand ändert nichts.
 */
export function nextEndedRuns<TMessage extends SessionMessageLike>(
  endedRuns: Record<string, RunEnd>,
  record: SessionResearchRecord<TMessage> | null | undefined,
): Record<string, RunEnd> {
  const questionId = record?.userMessage.messageId;
  if (!record || !questionId || record.status === 'pending') return endedRuns;
  if (record.status === 'failed') {
    if (!record.runEnd || endedRuns[questionId] === record.runEnd) return endedRuns;
    return { ...endedRuns, [questionId]: record.runEnd };
  }
  return forgetEndedRun(endedRuns, questionId);
}

/** „Wiederholen“ beginnt: der Status der Frage weicht dem neuen Lauf. */
export function forgetEndedRun(endedRuns: Record<string, RunEnd>, questionId: string): Record<string, RunEnd> {
  if (!(questionId in endedRuns)) return endedRuns;
  const next = { ...endedRuns };
  delete next[questionId];
  return next;
}

/** Der Status unter einer Frage; solange für sie ein Lauf in diesem Tab läuft, keiner. */
export function runEndForQuestion<TMessage extends SessionMessageLike>(
  questionId: string,
  endedRuns: Record<string, RunEnd>,
  record: SessionResearchRecord<TMessage> | null | undefined,
): RunEnd | undefined {
  if (record?.userMessage.messageId === questionId) {
    return record.status === 'failed' ? record.runEnd : undefined;
  }
  return endedRuns[questionId];
}

/**
 * Die beendeten Läufe der ausführlichen Schicht eines Chats (Ticket 09): endet einer im geöffneten Chat, ist sein Ergebnis
 * gesehen und die Seitenleiste setzt dafür keinen Punkt „neu“.
 */
export function endedDetailRunIds(
  detailRuns: Record<string, { sessionId: string; runId?: string; state: { status: string } }>,
  sessionId: string | null,
): string[] {
  return Object.values(detailRuns)
    .filter(record => record.sessionId === sessionId && record.runId && record.state.status !== 'loading')
    .map(record => record.runId!);
}
